/**
 * Onboarding 4-ogencontrole — types + Zod-schemas voor PR C3.
 *
 * Bron: spec C §3.3 (review-rij per (onboarding, rol, ronde)) + §3.4
 * (2 rondes: na medewerker-stappen, en na asset-toewijzing).
 *
 * Twee rollen × twee rondes = 4 review-rijen max per onboarding:
 *   Ronde 1: RECRUITER_TEAMLEIDER + HR_KASPER → onboarding van IN_BEHANDELING
 *            naar WACHT_OP_HANDTEKENING.
 *   Ronde 2: RECRUITER_TEAMLEIDER + HR_KASPER → onboarding van ASSETS_TOEWIJZEN
 *            naar GEREED_VOOR_START.
 *
 * Bij afkeur door één van beide reviewers valt de onboarding terug naar de
 * vorige status (IN_BEHANDELING resp. IN_CONTROLE). Spec §16.2 verbiedt
 * dezelfde reviewer beide rollen binnen één ronde.
 *
 * De DTOs hier maken geen permissie-aannames — die zitten in de API-route
 * (isKasperOfHR-gate, requirePermission). Permission-key: `ONBOARDING` met
 * `canApprove`-flag (de module bestaat al, zie `apps/api/src/lib/permissions.ts`).
 */

import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma `OnboardingReviewRol` / `Status`) ────────────────

export const ONBOARDING_REVIEW_ROL = ['RECRUITER_TEAMLEIDER', 'HR_KASPER'] as const
export type OnboardingReviewRol = (typeof ONBOARDING_REVIEW_ROL)[number]

export const ONBOARDING_REVIEW_STATUS = ['IN_AFWACHTING', 'GOEDGEKEURD', 'AFGEKEURD'] as const
export type OnboardingReviewStatus = (typeof ONBOARDING_REVIEW_STATUS)[number]

export const ONBOARDING_REVIEW_ROL_LABELS: Record<OnboardingReviewRol, string> = {
  RECRUITER_TEAMLEIDER: 'Recruiter / Teamleider',
  HR_KASPER:            'HR / Kasper (eigenaar)',
}

export const ONBOARDING_REVIEW_STATUS_LABELS: Record<OnboardingReviewStatus, string> = {
  IN_AFWACHTING: 'In afwachting',
  GOEDGEKEURD:   'Goedgekeurd',
  AFGEKEURD:     'Afgekeurd',
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

/**
 * Eén review-rij. `beoordeeldOp` is null zolang `status === 'IN_AFWACHTING'`.
 * `reviewerNaam` is een gederiveerd veld via User-lookup voor lijst-rendering
 * zonder extra join aan client-zijde.
 */
export interface OnboardingReviewDTO {
  id:             string
  onboardingId:   string
  reviewerUserId: string
  reviewerNaam:   string | null
  rol:            OnboardingReviewRol
  ronde:          number
  status:         OnboardingReviewStatus
  opmerkingen:    string | null
  beoordeeldOp:   string | null
  createdAt:      string
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

/**
 * POST `/onboarding/:id/review` — maak een nieuwe review-rij aan (rol + ronde
 * + toegewezen reviewer). Spec §3.3: één rij per (onboardingId, rol, ronde).
 * De API-route weigert duplicates via de DB-unique-constraint.
 *
 * `ronde` default 1 — backoffice UI maakt ronde-1-rijen aan zodra de
 * medewerker zijn deel heeft afgerond. Ronde-2-rijen worden door de API
 * automatisch aangemaakt bij overgang naar ASSETS_TOEWIJZEN (zie spec §3.4
 * en helper `createRonde2Reviews()` in de route).
 */
export const createOnboardingReviewSchema = z.object({
  rol:            z.enum(ONBOARDING_REVIEW_ROL),
  reviewerUserId: idParam('reviewerUserId'),
  ronde:          z.union([z.literal(1), z.literal(2)]).default(1),
})
export type CreateOnboardingReviewInput = z.infer<typeof createOnboardingReviewSchema>

/**
 * POST `/onboarding/:id/review/:reviewId/goedkeuren` — optionele toelichting.
 */
export const goedkeurOnboardingReviewSchema = z.object({
  opmerkingen: z.string().trim().max(2000).optional(),
})
export type GoedkeurOnboardingReviewInput = z.infer<typeof goedkeurOnboardingReviewSchema>

/**
 * POST `/onboarding/:id/review/:reviewId/afkeuren` — reden verplicht
 * (analoog aan spec §3.3 voor de stap-afwijs-flow). De reden wordt aan de
 * andere reviewer + aan HR zichtbaar gemaakt en gelogd in OnboardingAudit.
 */
export const afkeurOnboardingReviewSchema = z.object({
  reden: z.string().trim().min(1, 'Reden is verplicht').max(2000),
})
export type AfkeurOnboardingReviewInput = z.infer<typeof afkeurOnboardingReviewSchema>
